/**
 * DataEntryCard — one file row as an entity card (ADR-0037, Turbo's DataEntryCard): what the agent
 * understood, who it belongs to, and every detail it will write. Built for the keyboard: Enter
 * approves, Shift+Enter goes back, 1–4 picks a candidate, Alt+1..4 / Alt+0 change the kind.
 * Phase 3: currency + rate, installments, a split into categories and the bank statement line.
 */
import { useCallback, useMemo, useState, type RefObject } from "react";
import {
  Avatar,
  Banner,
  Button,
  Checkbox,
  ComboBox,
  Field,
  IconButton,
  Kbd,
  Segmented,
  Select,
  Tag,
  TextInput,
  Tooltip,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import {
  aiDataEntryApi,
  type CounterpartyDto,
  type Currency,
  type DataEntryLookupsDto,
  type LearnChange,
  type PaymentMethod,
  type SimilarGroupDto,
} from "../../../api/aiDataEntryApi";
import {
  agorot,
  canSplit,
  CURRENCY_OPTIONS,
  describeChange,
  KIND_META,
  KIND_ORDER,
  MATCH_LABELS,
  money,
  PAYMENT_OPTIONS,
  rowProblems,
  rowState,
  shortDate,
  setSplitAmount,
  SOURCE_LABELS,
  startSplit,
  STATE_META,
  wantedCounterpartyType,
  type LocalRow,
} from "./dataEntryModel";

export type FieldRefs = { amount: RefObject<HTMLInputElement>; date: RefObject<HTMLInputElement>; payment: RefObject<HTMLSpanElement> };
export type LearnState = { index: number; change: LearnChange; groups: SimilarGroupDto[]; busy: boolean };

function categoryOptions(categories: DataEntryLookupsDto["categories"]) {
  const byId = new Map(categories.map((c) => [c.id, c]));
  return categories.map((c) => {
    const parent = c.parentId ? byId.get(c.parentId) : null;
    return { value: c.id, label: parent ? `${parent.name} › ${c.name}` : c.name };
  });
}

function CounterpartyBlock({
  row,
  sessionId,
  onPick,
  onCreate,
  similarCount,
  onApplySimilar,
}: {
  row: LocalRow;
  sessionId: string;
  onPick: (cp: CounterpartyDto | null) => void;
  onCreate: () => void;
  similarCount: number;
  onApplySimilar: () => void;
}) {
  const want = wantedCounterpartyType(row.kind);
  const cp = row.counterparty;
  const candidates = row.candidates.filter((c) => !want || c.type === want);
  // stable identity: ComboBox restarts a running search whenever onSearch changes
  const search = useCallback(async (q: string) => (!want || q.length < 2 ? [] : aiDataEntryApi.search(sessionId, want, q)), [want, sessionId]);
  if (!want) return null;
  const who = want === "supplier" ? "ספק" : "לקוח";
  const optional = row.kind === "income" || row.kind === "expense";
  return (
    <section className="de-sec" aria-label="שיוך">
      <div className="de-sec__h">
        <b>שיוך ל{who}</b>
        {optional ? <span className="de-muted">רשות</span> : null}
      </div>
      {cp ? (
        <div className={`de-cp ${cp.confirmed === false ? "is-weak" : ""}`}>
          <Avatar text={cp.label} size={34} />
          <div className="de-cp__main">
            <b>{cp.label}</b>
            <span className="de-muted">{[cp.sub, MATCH_LABELS[cp.matchType] ?? null].filter(Boolean).join(" · ")}</span>
          </div>
          {cp.confirmed === false && !cp.ambiguous ? (
            <Tag tone="warning" icon="alert">
              התאמה חלקית
            </Tag>
          ) : null}
          <IconButton size="sm" icon="x" label="ביטול השיוך" onClick={() => onPick(null)} />
        </div>
      ) : null}
      {candidates.length && (!cp || cp.ambiguous || cp.confirmed === false) ? (
        <div className="de-cands" role="listbox" aria-label="התאמות מוצעות">
          {candidates.map((c, i) => (
            <button
              key={`${c.type}:${c.id}`}
              type="button"
              className={`de-cand ${cp && cp.id === c.id && cp.type === c.type ? "is-on" : ""}`}
              onClick={() => onPick({ ...c, confirmed: true, ambiguous: false })}
            >
              <Kbd>{i + 1}</Kbd>
              <span className="de-cand__t">
                <b>{c.label}</b>
                <small>{[c.sub, MATCH_LABELS[c.matchType]].filter(Boolean).join(" · ")}</small>
              </span>
              <span className="de-cand__score">{Math.round(c.score * 100)}%</span>
            </button>
          ))}
        </div>
      ) : null}
      <div className="de-cp__tools">
        <ComboBox
          value={null}
          onChange={(o: CounterpartyDto | null) => o && onPick({ ...o, score: 1, matchType: "manual", confirmed: true, ambiguous: false })}
          onSearch={search}
          minChars={2}
          placeholder={`חיפוש ${who}…`}
          ariaLabel={`חיפוש ${who}`}
          getKey={(o: CounterpartyDto) => `${o.type}:${o.id}`}
          renderOption={(o: CounterpartyDto) => (
            <span className="de-cand__t">
              <b>{o.label}</b>
              {o.sub ? <small>{o.sub}</small> : null}
            </span>
          )}
        />
        {!cp ? (
          <Button size="sm" icon="plus" onClick={onCreate}>
            {row.newEntity?.name ? `הוספת «${row.newEntity.name}»` : `${who} חדש`}
          </Button>
        ) : null}
      </div>
      {cp && similarCount ? (
        <button type="button" className="ck-link de-similar" onClick={onApplySimilar}>
          לשייך גם {similarCount} שורות דומות ל{cp.label}
        </button>
      ) : null}
    </section>
  );
}

/** "learn from the correction": offer the change the user just made to similar rows */
function LearnBlock({ learn, lookups, onApply, onDismiss }: { learn: LearnState | null; lookups: DataEntryLookupsDto; onApply: (g: SimilarGroupDto, save: boolean) => void; onDismiss: () => void }) {
  const [save, setSave] = useState(false);
  if (!learn?.groups.length) return null;
  return (
    <div className="de-learn" role="status">
      <div className="de-learn__h">
        <span aria-hidden="true">✦</span>
        <b>להחיל «{describeChange(learn.change, lookups)}» גם על שורות דומות?</b>
        <span className="ck-grow" />
        <IconButton size="sm" icon="x" label="לא, רק בשורה הזו" onClick={onDismiss} />
      </div>
      <div className="de-learn__groups">
        {learn.groups.map((g) => (
          <Button key={g.key} size="sm" variant="primary" disabled={learn.busy} onClick={() => onApply(g, save && g.by !== "description")}>
            {g.label} · {g.count} שורות
          </Button>
        ))}
      </div>
      <Checkbox checked={save} onChange={setSave} label="ולזכור ככלל — גם בקבצים הבאים" />
    </div>
  );
}

export type CardProps = {
  row: LocalRow;
  position: number;
  total: number;
  sessionId: string;
  lookups: DataEntryLookupsDto;
  hasLedger: boolean;
  flash: boolean;
  fieldRefs: FieldRefs;
  onChange: (patch: Partial<LocalRow>, field: string) => void;
  onPick: (cp: CounterpartyDto | null) => void;
  onCreate: () => void;
  similarCount: number;
  onApplySimilar: () => void;
  onApprove: () => void;
  onBack: () => void;
  onUndo: () => void;
  learn: LearnState | null;
  onLearnApply: (g: SimilarGroupDto, save: boolean) => void;
  onLearnDismiss: () => void;
  onAsk: () => void;
};

export default function DataEntryCard({
  row,
  position,
  total,
  sessionId,
  lookups,
  hasLedger,
  flash,
  fieldRefs,
  onChange,
  onPick,
  onCreate,
  similarCount,
  onApplySimilar,
  onApprove,
  onBack,
  onUndo,
  learn,
  onLearnApply,
  onLearnDismiss,
  onAsk,
}: CardProps) {
  const meta = KIND_META[row.kind];
  const state = rowState(row);
  const problems = rowProblems(row, { hasLedger });
  const locked = ["done", "saving", "kept"].includes(state);
  const catOptions = useMemo(() => categoryOptions(lookups.categories), [lookups.categories]);
  const kinds = KIND_ORDER.filter((k) => lookups.kinds.includes(k) || k === row.kind);
  const problemOf = (field: string) => problems.find((p) => p.field === field)?.text;
  const setCategory = (id: string) => {
    const c = lookups.categories.find((x) => x.id === id);
    onChange({ category: c ? { id: c.id, name: c.name, source: "manual" } : null }, "category");
  };
  const dirSign = meta.dir === "out" ? "−" : meta.dir === "in" ? "+" : "";
  const currency = row.currency ?? "ILS";
  const foreign = currency !== "ILS";
  const splits = canSplit(row) ? row.categorySplits : null;
  const title = row.counterparty?.label || row.newEntity?.name || row.source || `שורה ${row.line}`;
  const deal = row.deals[0];

  return (
    <article className={`de-card ${flash ? "is-flash" : ""} is-${state}`} aria-label={`שורה ${row.line}`} tabIndex={-1}>
      <header className="de-card__h">
        <Avatar text={title} size={44} square />
        <div className="de-card__titles">
          <h3>{title}</h3>
          <div className="de-card__meta">
            <span>
              {shortDate(row.date)} · שורה {row.line}
            </span>
            {row.source && row.source !== title ? (
              <span className="de-card__src" title={row.source}>
                «{row.source}»
              </span>
            ) : null}
          </div>
        </div>
        <div className={`de-amount de-amount--${meta.dir ?? "none"}`}>
          <span className="de-amount__v">
            {dirSign}
            {money(row.amount, currency)}
          </span>
        </div>
      </header>
      <div className="de-card__tags">
        {row.ruleApplied ? (
          <Tag tone="violet" icon="sliders" title={row.ruleApplied}>
            כלל
          </Tag>
        ) : null}
        <Tag tone={STATE_META[state].tone}>{STATE_META[state].label}</Tag>
        {row.installments && row.installments > 1 ? <Tag icon="card">{row.installments} תשלומים</Tag> : null}
        {foreign ? <Tag tone="info">{`${currency}${row.exchangeRate ? ` · שער ${row.exchangeRate}` : " · בלי שער"}`}</Tag> : null}
        {splits?.length ? <Tag tone="violet">פיצול ל-{splits.length} קטגוריות</Tag> : null}
        <span className="ck-grow" />
        <span className="de-muted">
          {position} מתוך {total}
        </span>
      </div>

      <div className="de-kind">
        <Segmented
          ariaLabel="סוג השורה"
          value={row.kind}
          onChange={(k) => !locked && !row.existing && onChange({ kind: k as LocalRow["kind"] }, "kind")}
          items={kinds.map((k) => ({ value: k, label: KIND_META[k].label }))}
        />
        <span className="de-muted de-kind__why">
          <span aria-hidden="true">✦ </span>
          {row.kindReason}
          {row.kindConfidence ? ` · ${Math.round(row.kindConfidence * 100)}%` : ""}
          <button type="button" className="ck-link de-ask" onClick={onAsk}>
            שאלה / תיקון לסוכן
          </button>
          <span className="de-hint"> · Alt+1–4 לשינוי, Alt+0 לדילוג</span>
        </span>
      </div>

      {!locked ? <LearnBlock learn={learn} lookups={lookups} onApply={onLearnApply} onDismiss={onLearnDismiss} /> : null}
      {row.commit.state === "error" ? <Banner tone="danger">{row.commit.error}</Banner> : null}
      {state === "done" ? (
        <Banner
          tone="success"
          action={
            <Button size="sm" icon="undo" onClick={onUndo}>
              ביטול ועריכה
            </Button>
          }
        >
          {row.commit.state === "done" && row.commit.result.deal
            ? `נרשם כתשלום על העסקה ${deal?.label ?? ""}.`
            : row.commit.state === "done" && row.commit.result.parts
              ? `נרשם במערכת — ${row.commit.result.parts} רשומות (פיצול לקטגוריות).`
              : "נרשם במערכת."}
          {row.commit.state === "done" && row.commit.result.bank?.matched ? " הותאם לשורת הבנק." : ""}
          {row.commit.state === "done" && row.commit.result.bank?.error ? ` ההתאמה לבנק לא בוצעה: ${row.commit.result.bank.error}` : ""}
        </Banner>
      ) : null}
      {state === "kept" ? (
        <Banner
          tone="success"
          action={
            <Button size="sm" icon="undo" onClick={onUndo}>
              שינוי
            </Button>
          }
        >
          הרשומה הקיימת נשארה כמו שהיא.
        </Banner>
      ) : null}

      {row.existing && row.kind !== "skip" ? (
        <fieldset className="de-body" disabled={locked}>
          <section className="de-sec de-existing" aria-label="רשומה קיימת">
            <Banner tone="info">
              <div className="de-existing__h">
                <b>
                  {row.existing.table === "payment" ? "תשלום" : row.existing.table === "income" ? "הכנסה" : "הוצאה"} שכבר קיימת במערכת
                </b>
                <span className="de-muted">
                  לפי אסמכתא + סכום · {money(row.existing.amount)} · {shortDate(row.existing.date)}
                  {row.existing.label ? ` · ${row.existing.label}` : ""}
                </span>
              </div>
            </Banner>
            <Segmented
              ariaLabel="מה לעשות עם הרשומה הקיימת"
              value={row.existingAction ?? "keep"}
              onChange={(v) => onChange({ existingAction: v as LocalRow["existingAction"] }, "existing")}
              items={[
                { value: "keep", label: "להשאיר כמו שזה" },
                { value: "create", label: "לרשום בכל זאת" },
              ]}
            />
            {row.existingAction !== "create" ? <span className="de-muted">שום רשומה חדשה לא תיווצר מהשורה הזו.</span> : null}
          </section>
        </fieldset>
      ) : null}

      {row.kind !== "skip" && (!row.existing || row.existingAction === "create") ? (
        <fieldset className="de-body" disabled={locked}>
          {row.duplicate ? (
            <Banner tone={row.duplicate.strength === "strong" || row.duplicate.inFile ? "warning" : "info"}>
              <div className="de-dup">
                <span>
                  {row.duplicate.inFile
                    ? `${row.duplicate.label} — כנראה כפולה.`
                    : `ייתכן שכבר רשום: ${row.duplicate.label} · ${money(row.duplicate.amount)} · ${shortDate(row.duplicate.date)}`}
                </span>
                <Segmented
                  ariaLabel="טיפול בכפילות"
                  value={row.duplicateAction ?? ""}
                  onChange={(v) => onChange({ duplicateAction: v as LocalRow["duplicateAction"] }, "duplicate")}
                  items={[
                    { value: "skip", label: "לא לרשום" },
                    { value: "create", label: "לרשום בכל זאת" },
                  ]}
                />
              </div>
            </Banner>
          ) : null}

          {row.duplicateAction !== "skip" ? (
            <>
              <CounterpartyBlock row={row} sessionId={sessionId} onPick={onPick} onCreate={onCreate} similarCount={similarCount} onApplySimilar={onApplySimilar} />

              {row.kind === "payment" && deal ? (
                <div className={`de-deal ${row.useDeal ? "is-on" : ""}`}>
                  <span>
                    <b>עסקה פתוחה</b>
                    <span className="de-muted">
                      {" "}
                      · {deal.label} · יתרה {agorot(deal.balanceAgorot)}
                    </span>
                  </span>
                  <span className="ck-grow" />
                  <Button size="sm" variant={row.useDeal ? "primary" : "default"} icon={row.useDeal ? "check" : "link"} onClick={() => onChange({ useDeal: !row.useDeal }, "deal")}>
                    {row.useDeal ? "יירשם כתשלום על העסקה" : "לרשום על העסקה"}
                  </Button>
                </div>
              ) : null}

              <section className="de-sec" aria-label="פרטים">
                <div className="de-grid">
                  <Field label="סכום" error={problemOf("amount")}>
                    <TextInput
                      ref={fieldRefs.amount}
                      type="number"
                      step="0.01"
                      ltr
                      value={row.amount ?? ""}
                      onChange={(e) => onChange({ amount: e.target.value === "" ? null : Number(e.target.value) }, "amount")}
                    />
                  </Field>
                  <Field label="תאריך" error={problemOf("date")}>
                    <TextInput ref={fieldRefs.date} type="date" value={row.date ?? ""} onChange={(e) => onChange({ date: e.target.value || null }, "date")} />
                  </Field>
                  <Field label="אמצעי תשלום" hint={row.paymentSource ? SOURCE_LABELS[row.paymentSource] : null} error={problemOf("payment")}>
                    <span ref={fieldRefs.payment} className="de-focus-wrap">
                      <Select
                        value={row.paymentMethod ?? ""}
                        placeholder="בחירה…"
                        options={PAYMENT_OPTIONS}
                        onChange={(v) => onChange({ paymentMethod: (v || null) as PaymentMethod | null, paymentSource: "manual" }, "payment")}
                      />
                    </span>
                  </Field>
                  <Field full label="תיאור" hint="הצעת הסוכן — אפשר לשנות">
                    <TextInput value={row.description ?? ""} onChange={(e) => onChange({ description: e.target.value }, "description")} />
                  </Field>
                  {!splits?.length ? (
                    <Field label="קטגוריה" hint={row.category?.source ? (SOURCE_LABELS[row.category.source] ?? null) : null}>
                      <Select value={row.category?.id ?? ""} placeholder="ללא" options={[{ value: "", label: "ללא" }, ...catOptions]} onChange={setCategory} />
                    </Field>
                  ) : null}
                  {row.kind === "income" || row.kind === "expense" ? (
                    <Field label="קרן" hint={row.fund?.source ? (SOURCE_LABELS[row.fund.source] ?? null) : hasLedger ? null : "בחרו קופה"} error={problemOf("ledger")}>
                      <Select
                        value={row.fund?.id ?? ""}
                        placeholder="ברירת מחדל"
                        disabled={!hasLedger}
                        options={[{ value: "", label: "ברירת מחדל" }, ...lookups.funds.map((f) => ({ value: f.id, label: f.name }))]}
                        onChange={(v) => {
                          const f = lookups.funds.find((x) => x.id === v);
                          onChange({ fund: f ? { id: f.id, name: f.name, source: "manual" } : null }, "fund");
                        }}
                      />
                    </Field>
                  ) : null}
                  {row.kind === "sale" ? (
                    <>
                      <Field label="מוצר / קורס" hint={row.product?.source ? (SOURCE_LABELS[row.product.source] ?? null) : "בלי מוצר — התיאור הוא שם העסקה"}>
                        <Select
                          value={row.product?.id ?? ""}
                          placeholder="ללא"
                          options={[{ value: "", label: "ללא" }, ...lookups.products.map((p) => ({ value: p.id, label: `${p.name} · ${agorot(p.priceAgorot)}` }))]}
                          onChange={(v) => {
                            const p = lookups.products.find((x) => x.id === v);
                            onChange({ product: p ? { id: p.id, name: p.name, source: "manual" } : null }, "product");
                          }}
                        />
                      </Field>
                      <Field label="נציג">
                        <Select
                          value={row.rep?.id ?? ""}
                          placeholder="ללא נציג"
                          options={[{ value: "", label: "ללא נציג" }, ...lookups.reps.map((r) => ({ value: r.id, label: r.name }))]}
                          onChange={(v) => {
                            const r = lookups.reps.find((x) => x.id === v);
                            onChange({ rep: r ? { id: r.id, name: r.name } : null }, "rep");
                          }}
                        />
                      </Field>
                    </>
                  ) : null}
                  <Field label="אסמכתא">
                    <TextInput ltr value={row.reference ?? ""} onChange={(e) => onChange({ reference: e.target.value || null }, "reference")} />
                  </Field>
                  <Field
                    label="מטבע"
                    hint={foreign ? (row.kind === "sale" ? "העסקה נשמרת במטבע הזה עם השער" : "נרשם במטבע ובסכום המקוריים") : null}
                  >
                    <Select
                      value={currency}
                      options={(lookups.currencies ?? CURRENCY_OPTIONS.map((c) => c.value)).map((v) => CURRENCY_OPTIONS.find((c) => c.value === v) ?? { value: v, label: v })}
                      onChange={(v) => onChange({ currency: (v || "ILS") as Currency }, "currency")}
                    />
                  </Field>
                  {foreign ? (
                    <Field label={`שער המרה (₪ ל-1 ${currency})`} hint={row.exchangeRate ? null : row.kind === "sale" ? "בלי שער — שער ברירת המחדל של המכירות" : "רשות — נשמר בהערות"} error={problemOf("currency")}>
                      <TextInput
                        type="number"
                        step="0.0001"
                        ltr
                        value={row.exchangeRate ?? ""}
                        onChange={(e) => onChange({ exchangeRate: e.target.value === "" ? null : Number(e.target.value) || null }, "exchangeRate")}
                      />
                    </Field>
                  ) : null}
                  {row.kind === "payment" || row.kind === "income" || row.kind === "sale" ? (
                    <Field label="מספר תשלומים" hint={row.kind === "sale" ? "נשמר בעסקה" : row.installments && row.installments > 1 ? "נשמר בהערות הרשומה" : null}>
                      <TextInput
                        type="number"
                        min="1"
                        max={row.kind === "sale" ? "60" : "120"}
                        ltr
                        placeholder="1"
                        value={row.installments ?? ""}
                        onChange={(e) =>
                          onChange({ installments: e.target.value ? Math.max(1, Math.min(row.kind === "sale" ? 60 : 120, Math.round(Number(e.target.value)) || 1)) : null }, "installments")
                        }
                      />
                    </Field>
                  ) : null}
                </div>
                {canSplit(row) ? (
                  splits?.length ? (
                    <div className="de-split" aria-label="פיצול לקטגוריות">
                      <b>פיצול לקטגוריות</b>
                      {splits.map((x, i) => (
                        <div key={i} className="de-split__row">
                          <Select
                            value={x.categoryId ?? ""}
                            placeholder="קטגוריה…"
                            options={catOptions}
                            aria-label={`קטגוריה לחלק ${i + 1}`}
                            onChange={(v) => onChange({ categorySplits: splits.map((y, j) => (j === i ? { ...y, categoryId: v || null } : y)) }, "split")}
                          />
                          <TextInput
                            type="number"
                            step="0.01"
                            ltr
                            aria-label={`סכום לחלק ${i + 1}`}
                            value={x.amount}
                            onChange={(e) => onChange({ categorySplits: setSplitAmount(splits, i, Number(e.target.value) || 0, Number(row.amount) || 0) }, "split")}
                          />
                          {splits.length > 2 ? (
                            <IconButton size="sm" icon="x" label="הסרת החלק" onClick={() => onChange({ categorySplits: splits.filter((_, j) => j !== i) }, "split")} />
                          ) : null}
                        </div>
                      ))}
                      {problemOf("split") ? <span className="de-err">{problemOf("split")}</span> : null}
                      <div className="de-split__tools">
                        {splits.length < 10 ? (
                          <button type="button" className="ck-link" onClick={() => onChange({ categorySplits: [...splits, { categoryId: null, amount: 0 }] }, "split")}>
                            + חלק נוסף
                          </button>
                        ) : null}
                        <button type="button" className="ck-link" onClick={() => onChange({ categorySplits: null }, "split")}>
                          ביטול הפיצול
                        </button>
                        <span className="de-muted">כל חלק נרשם כרשומה נפרדת עם אותה אסמכתא</span>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="ck-link de-split-open" onClick={() => onChange({ categorySplits: startSplit(row) }, "split")}>
                      פיצול לשתי קטגוריות
                    </button>
                  )
                ) : null}
                {row.kind === "sale" ? <Checkbox checked={row.salePaid} onChange={(v) => onChange({ salePaid: v }, "salePaid")} label="התשלום התקבל (נרשם תשלום מלא על העסקה)" /> : null}
              </section>

              {row.bankLine && canSplit(row) ? (
                <div className={`de-bank-line ${row.useBank ? "is-on" : ""}`}>
                  <span className="de-bank-line__ic" aria-hidden="true">
                    🏦
                  </span>
                  <span>
                    <b>שורת בנק פתוחה תואמת</b>
                    <span className="de-muted">
                      {" "}
                      · {shortDate(row.bankLine.date)} · {money(row.bankLine.amount)} · {row.bankLine.description}
                    </span>
                  </span>
                  <span className="ck-grow" />
                  <Button size="sm" variant={row.useBank ? "primary" : "default"} icon={row.useBank ? "check" : "link"} onClick={() => onChange({ useBank: !row.useBank }, "bank")}>
                    {row.useBank ? "תותאם לשורת הבנק" : "להתאים לשורת הבנק"}
                  </Button>
                </div>
              ) : null}

              {row.recurrence || row.lastSeen ? (
                <div className="de-facts">
                  {row.recurrence ? (
                    <Tag tone="info" icon="repeat">
                      מחזורי · {row.recurrence.label}
                    </Tag>
                  ) : null}
                  {row.lastSeen?.date ? (
                    <Tag icon="history">
                      פעם קודמת: {money(row.lastSeen.amount)} · {shortDate(row.lastSeen.date)}
                    </Tag>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : null}
        </fieldset>
      ) : null}
      {row.kind === "skip" ? <Banner tone="info">השורה לא תירשם{row.kindReason ? ` (${row.kindReason})` : ""}. Enter ממשיך לשורה הבאה.</Banner> : null}

      {problems.length && !locked ? (
        <div className="de-problems" role="status">
          {problems.map((p) => (
            <Tag key={p.text} tone="warning" icon="alert">
              {p.text}
            </Tag>
          ))}
        </div>
      ) : null}

      <footer className="de-card__f">
        <Tooltip title="Shift+Enter">
          <Button icon="right" onClick={onBack} disabled={position <= 1}>
            הקודמת
          </Button>
        </Tooltip>
        <span className="ck-grow" />
        <span className="de-hint">
          <Kbd>Enter</Kbd> אישור והבאה · <Kbd>Shift</Kbd>+<Kbd>Enter</Kbd> אחורה · <Kbd>PgDn</Kbd> בלי לאשר
        </span>
        <Button variant="primary" icon="check" onClick={onApprove} disabled={state === "saving"}>
          {state === "done" || state === "kept"
            ? "הבאה"
            : row.existing && row.existingAction !== "create"
              ? "הבאה (ללא שינוי)"
              : row.kind === "skip" || row.duplicateAction === "skip"
                ? "דילוג והבאה"
                : "אישור והבאה"}
        </Button>
      </footer>
    </article>
  );
}
